import { DateTime } from 'luxon';
import { ensureDateTime } from './time-utils';

export const YyMmDdFormat = 'yyyyMMdd';
const DateFormat = 'dd-MMM-yyyy';
const DayMonthFormat = 'dd-MMM';
const TimeFormat = 'HH:mm';
const TimeOfDayFormat = 'h:mm a';
const TimeFormat_WithSeconds = 'HH:mm:ss';
const DateTimeFormat = `${DateFormat} ${TimeFormat}`;
const DateTimeWithSecondsFormat = `${DateFormat} ${TimeFormat_WithSeconds}`;

export const formatDateOrNull = (value: unknown): string => {
	return formatDateWithFormat(value, DateFormat);
};

export const formatDdMonth = (value: unknown): string => {
	return formatDateWithFormat(value, DayMonthFormat);
};

export const formatYyMmDd = (value: unknown): string => {
	return formatDateWithFormat(value, YyMmDdFormat);
};

const formatDateWithFormat = (value: unknown, format: string): string => {
	if (!value) {
		return '';
	}

	if (typeof value === 'object') {
		if (value instanceof Date) {
			const dt = DateTime.fromJSDate(value);
			return dt.toFormat(format);
		}

		if (value instanceof DateTime) {
			if (!value.isValid) {
				return '';
			}
			return value.toFormat(format);
		}
	}

	return 'Unknown format';
};

export const formatDateTimeOrNull = (value: unknown, opts?: { withSeconds: boolean }): string => {
	const format = opts?.withSeconds ? DateTimeWithSecondsFormat : DateTimeFormat;
	return formatDateWithFormat(value, format);
};

export const formatHoursMins = (value: Date | DateTime): string => {
	const dt = ensureDateTime(value);
	return dt.toFormat('HHmm');
};

export const formatTimeOrNull = (value: unknown, opts?: { withSeconds: boolean }): string => {
	if (!value) {
		return '';
	}

	const format = opts?.withSeconds ? TimeFormat_WithSeconds : TimeFormat;

	if (typeof value === 'object') {
		if (value instanceof Date) {
			const dt = DateTime.fromJSDate(value);
			return dt.toFormat(format);
		}

		if (value instanceof DateTime) {
			return value.toFormat(format);
		}
	}

	return 'Unknown format';
};

export const formatTimeValueOrNull = (value: unknown): string => {
	if (value === undefined || value === null) {
		return '';
	}

	let number = 0;
	if (typeof value === 'string') {
		number = Number(value);
	} else if (typeof value === 'number') {
		number = value;
	} else {
		return 'Unknown format';
	}

	const formatted = number.toFixed(2).replace('.', ':');
	return formatted;
};

export const formatTimeOfDay = (value: unknown): string => {
	if (!value) {
		return '';
	}

	if (typeof value === 'object') {
		if (value instanceof Date) {
			const dt = DateTime.fromJSDate(value);
			return dt.toFormat(TimeOfDayFormat);
		}

		if (value instanceof DateTime) {
			return value.toFormat(TimeOfDayFormat);
		}
	}

	return 'Unknown format';
};

/**
 * Formats a date range in the standard DD-MMM-YYYY format.
 * @param  {Date | DateTime} startDate
 * @param  {Date | DateTime} endDate
 * @param  {boolean} useHtmlBreak If true, separates the date with a </br> instead of a dash.
 */
export const formatDateRange = (startDate: Date | DateTime, endDate: Date | DateTime, useHtmlBreak = false): string => {
	return `${formatDateOrNull(startDate)}${useHtmlBreak ? '</br>' : ' - '}${formatDateOrNull(endDate)}`;
};

/**
 * Formats a time of day pair  - e.g. 12:00 - 14:00
 * @param timeStart
 * @param timeEnd
 * @returns
 */
export const formatTimes = (timeStart: Date | DateTime, timeEnd: Date | DateTime): string => {
	timeStart = ensureDateTime(timeStart);
	timeEnd = ensureDateTime(timeEnd);

	return `${formatTimeOrNull(timeStart)} - ${formatTimeOrNull(timeEnd)}`;
};

export const formatBeforeNowDuration = (serverTimeStamp: Date, timeStamp: Date) => {
	// Calculate the difference in time between the server time, when the item as fetched, and now.
	const serverTimeDiff = DateTime.now().diff(ensureDateTime(serverTimeStamp));

	// Calculate the total time between now and the timeStamp, taking into account the difference between
	// server time when the time was fetched, and now.
	const since = DateTime.fromJSDate(serverTimeStamp).diff(ensureDateTime(timeStamp));

	// Add the two together to get the total time between now and the timeStamp.
	const totalSince = serverTimeDiff.milliseconds + since.milliseconds;

	// Convert to a human-readable relative time.
	const result = DateTime.now()
		.minus({ millisecond: totalSince })
		.toRelative()
		?.replace(/^0 seconds ago/, 'just now');

	return result;
};

/**
 * Formats a Date or DateTime to return the day of the week.
 * @param date
 * @returns
 */
export const formatDayOfWeek = (date: Date | DateTime) => {
	if (!date) {
		return '';
	}

	const dt = ensureDateTime(date);
	return dt.toFormat('ccc');
};
